/**
 * Markdown → HTML, for display only.
 *
 * The content is the reader's own repository, so this is not a defence against a hostile
 * author — it is a defence against the *ordinary* case, where a document quotes an attack
 * it is describing. Several of the documents this surface renders do exactly that: a
 * research note on prompt injection contains the payloads it studies. Rendering those
 * unsanitised would execute a page's worth of examples.
 *
 * So: parse, then sanitise, always in that order and never optionally.
 */
import DOMPurify from "dompurify"
import { marked } from "marked"

marked.setOptions({ gfm: true, breaks: false })

/**
 * Frontmatter is metadata, and markdown has no idea.
 *
 * `---\nlane: product\n---` renders as a horizontal rule, a setext heading reading
 * "lane: product", and another rule — so the document opens with its own bookkeeping set
 * in display type. Stripped before parsing rather than hidden with CSS, because the
 * problem is that it was never content.
 */
const FRONTMATTER = /^\s*(?:<!--[\s\S]*?-->\s*)*---\r?\n[\s\S]*?\r?\n---\r?\n?/

export function stripFrontmatter(md: string): string {
  return md.replace(FRONTMATTER, "")
}

export function render(md: string): string {
  const html = marked.parse(stripFrontmatter(md), { async: false }) as string
  return DOMPurify.sanitize(html, {
    // No inline event handlers, no <script>, no <iframe>, no `javascript:` — DOMPurify's
    // defaults. The one addition is `target`, so an external link can open away from the
    // reader without losing the document they were in.
    ADD_ATTR: ["target", "rel"],
  })
}

/**
 * A document's own first heading, if it has one.
 *
 * The filename is a slug somebody chose; the H1 is the title they wrote. Prefer the title
 * and fall back to the slug — and drop it from the body, because a page that shows its
 * heading twice looks like a rendering bug.
 */
export function splitTitle(md: string): { title: string | null; body: string } {
  const text = stripFrontmatter(md)
  const m = /^\s*#\s+(.+?)\s*$/m.exec(text)
  // Only a heading that opens the document is its title. One further down is a section,
  // and lifting it would retitle the page with a fragment of its own middle.
  const before = text.slice(0, m?.index ?? 0).replace(/<!--[\s\S]*?-->/g, "").trim()
  if (!m || before !== "") return { title: null, body: text }
  return { title: m[1], body: text.slice(m.index + m[0].length) }
}
